import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/admin.css';

const getTodayInIsrael = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jerusalem' });

const getLast30DaysRange = () => {
  const today = getTodayInIsrael();
  const [year, month, day] = today.split('-').map(Number);
  const start = new Date(year, month - 1, day - 30);
  return {
    start_date: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
    end_date: today,
  };
};

const formatSentAt = (value) => (value
  ? new Date(value).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '--');

const formatDay = (value) => new Date(`${value}T12:00:00`).toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'short' });

const AdminEmailLog = () => {
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [range, setRange] = useState(getLast30DaysRange);
  const [sendingTest, setSendingTest] = useState(false);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/email-log', { params: range });
      setData(response.data.data);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load email log';
      setData(null);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const response = await api.post('/email-log/test');
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Test email failed', { autoClose: false });
    } finally {
      setSendingTest(false);
    }
  };

  const handleRangeChange = (field, value) => {
    if (!value) return;
    setRange(prev => {
      const next = { ...prev, [field]: value };
      if (next.start_date > next.end_date) {
        if (field === 'start_date') next.end_date = next.start_date;
        else next.start_date = next.end_date;
      }
      return next;
    });
  };

  if (currentUser?.role !== 'admin') {
    return <div className="loading">Access denied</div>;
  }

  const summary = data?.summary;

  return (
    <div className={`admin-page${loading ? ' stats-loading' : ''}`}>
      <div className="page-header">
        <h1>Email Log</h1>
        <div className="header-controls">
          <button className="btn-export btn-export-excel" onClick={sendTestEmail} disabled={sendingTest}>
            {sendingTest ? 'Sending...' : `Send test email to ${currentUser?.email || 'me'}`}
          </button>
          <button className="btn-export btn-export-excel" onClick={fetchLog} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      <div className="stats-filters daily-stats-page-filters">
        <div className="filter-group">
          <label>Missed day from</label>
          <input
            type="date"
            className="stats-date-input"
            value={range.start_date}
            onChange={(e) => handleRangeChange('start_date', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>To</label>
          <input
            type="date"
            className="stats-date-input"
            value={range.end_date}
            onChange={(e) => handleRangeChange('end_date', e.target.value)}
          />
        </div>
      </div>

      {data && !data.enabled && (
        <div className="modal-info email-log-warning">
          Reminder emails are turned off on this server (REMINDER_EMAILS_ENABLED is not "true").
        </div>
      )}

      {error && !loading && (
        <div className="admin-table-wrapper daily-stats-empty-state">
          {error}
        </div>
      )}

      {!error && (
        <>
          <div className="overview-cards daily-stats-overview">
            <div className="overview-card">
              <div className="card-icon blue">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : summary?.total ?? 0}</span>
                <span className="card-desc">Total</span>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon green">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : summary?.sent ?? 0}</span>
                <span className="card-desc">Sent</span>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon red">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : summary?.failed ?? 0}</span>
                <span className="card-desc">Failed</span>
              </div>
            </div>
          </div>

          <div className="section-title">Reminders</div>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Sent At</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Missed Day</th>
                  <th>Status</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="6" style={{ textAlign: 'center', color: '#999' }}>Loading...</td></tr>
                )}
                {!loading && data?.logs?.map((log) => (
                  <tr key={log.id}>
                    <td>{formatSentAt(log.sent_at)}</td>
                    <td>{log.name || '--'}</td>
                    <td>{log.email || '--'}</td>
                    <td>{formatDay(log.date)}</td>
                    <td><span className={`role-badge email-status-${log.status}`}>{log.status}</span></td>
                    <td className="email-log-error">{log.error || '--'}</td>
                  </tr>
                ))}
                {!loading && data?.logs?.length === 0 && (
                  <tr><td colSpan="6" style={{ textAlign: 'center', color: '#999' }}>No emails in this range</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminEmailLog;
